import Autocomplete from '@mui/material/Autocomplete';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import type { SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import type { CSSProperties, HTMLAttributes, ReactNode, SyntheticEvent } from 'react';

import { IconSearch } from '~/ui/core/icons';

type DropdownWithSearchProps<T> = {
    readonly getOptionLabel: (option: T) => string;
    readonly onFocus: () => void;
    readonly onOptionClick: (_: SyntheticEvent, newValue: T | null) => void;
    readonly onReset?: () => void;
    readonly onSearchChange: (value: string) => void;
    readonly placeholder?: string;
    readonly renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T) => ReactNode;
    readonly searchTerm: string;
    readonly suggestedEntities: readonly T[];
};

const textFieldStyles: SxProps = {
    ':focus-within': {
        border: '1px solid #FC531F',
    },
    backgroundColor: '#F6F6F7',
    border: '1px solid transparent',
    borderRadius: '0.714rem',
    fontFamily: "'FT Regola Neue'",
    gap: 1,
};

const iconButtonStyles: SxProps = {
    height: '1.286rem',
    marginRight: '0.571rem',
    padding: '0',
    width: '1.286rem',
};

const iconStyles: SxProps = {
    display: 'flex',
    height: '1.143rem',
    width: '1.143rem',
};

const textFieldInputStyles: CSSProperties = {
    fontFamily: "'FT Regola Neue'",
    fontSize: '0.875rem',
};

export const DropdownWithSearch = <T,>({
    getOptionLabel,
    onFocus,
    onOptionClick,
    onReset,
    onSearchChange,
    placeholder = 'Search...',
    renderOption,
    searchTerm,
    suggestedEntities,
}: DropdownWithSearchProps<T>): ReactNode => {
    return (
        <Autocomplete
            blurOnSelect={true}
            componentsProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 10],
                            },
                        },
                    ],
                },
            }}
            filterOptions={(options) => options}
            freeSolo={true}
            getOptionLabel={getOptionLabel}
            inputValue={searchTerm}
            options={suggestedEntities}
            renderInput={(params) => (
                <TextField
                    variant="standard"
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        endAdornment: null,
                        startAdornment: (
                            <IconButton disableRipple={true} sx={iconButtonStyles}>
                                <Icon sx={iconStyles}>
                                    <IconSearch />
                                </Icon>
                            </IconButton>
                        ),
                        style: textFieldInputStyles,
                    }}
                    placeholder={placeholder}
                    sx={textFieldStyles}
                />
            )}
            renderOption={renderOption}
            onChange={onOptionClick}
            onFocus={onFocus}
            onInputChange={(_, newValue, reason) => {
                if (reason === 'reset') {
                    onReset?.();
                } else {
                    onSearchChange(newValue);
                }
            }}
        />
    );
};
